export type ScheduleMode = 'disabled' | 'seconds' | 'minutes' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';
export type ScheduleFields = {
  mode: ScheduleMode;
  interval: string;
  time: string;
  weekday: string;
  monthday: string;
};

export const intervalChoices = ['1', '2', '5', '10', '15', '20', '30'];
export const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const integer = (value: string, min: number, max: number) => /^\d+$/.test(value) && Number(value) >= min && Number(value) <= max;

export const parseSchedule = (value: string): ScheduleFields => {
  const defaults: ScheduleFields = { mode: 'custom', interval: '5', time: '03:00', weekday: '1', monthday: '1' };
  if (!value.trim()) return { ...defaults, mode: 'disabled' };
  const fields = value.trim().split(/\s+/);
  if (fields.length === 5) fields.unshift('0');
  if (fields.length !== 6) return defaults;
  const [second, minute, hour, day, month, weekday] = fields;
  const interval = (field: string) => field === '*' ? '1' : /^\*\/\d+$/.test(field) ? field.slice(2) : '';
  if ([minute, hour, day, month, weekday].every(field => field === '*') && intervalChoices.includes(interval(second))) {
    return { ...defaults, mode: 'seconds', interval: interval(second) };
  }
  if (second !== '0') return defaults;
  if ([hour, day, month, weekday].every(field => field === '*') && intervalChoices.includes(interval(minute))) {
    return { ...defaults, mode: 'minutes', interval: interval(minute) };
  }
  if (!integer(minute, 0, 59)) return defaults;
  if (hour === '*' && [day, month, weekday].every(field => field === '*')) {
    return { ...defaults, mode: 'hourly', time: `00:${minute.padStart(2, '0')}` };
  }
  if (!integer(hour, 0, 23) || month !== '*') return defaults;
  const time = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  if (day === '*' && weekday === '*') return { ...defaults, mode: 'daily', time };
  if (day === '*' && integer(weekday, 0, 7)) return { ...defaults, mode: 'weekly', time, weekday: String(Number(weekday) % 7) };
  if (integer(day, 1, 31) && weekday === '*') return { ...defaults, mode: 'monthly', time, monthday: String(Number(day)) };
  return defaults;
};

export const formatSchedule = (fields: ScheduleFields): string | null => {
  if (fields.mode === 'disabled') return '';
  if (fields.mode === 'custom') return null;
  if (fields.mode === 'seconds' || fields.mode === 'minutes') {
    if (!intervalChoices.includes(fields.interval)) return null;
    const interval = fields.interval === '1' ? '*' : `*/${fields.interval}`;
    if (fields.mode === 'seconds') return `${interval} * * * * *`;
    return `0 ${interval} * * * *`;
  }
  if (!/^\d{2}:\d{2}$/.test(fields.time)) return null;
  const [hour, minute] = fields.time.split(':');
  if (!integer(hour, 0, 23) || !integer(minute, 0, 59)) return null;
  if (fields.mode === 'hourly') return `0 ${Number(minute)} * * * *`;
  if (fields.mode === 'daily') return `0 ${Number(minute)} ${Number(hour)} * * *`;
  if (fields.mode === 'weekly' && integer(fields.weekday, 0, 6)) return `0 ${Number(minute)} ${Number(hour)} * * ${fields.weekday}`;
  if (fields.mode === 'monthly' && integer(fields.monthday, 1, 31)) return `0 ${Number(minute)} ${Number(hour)} ${fields.monthday} * *`;
  return null;
};
